import * as R from '../../Router/routes';
import {
  AsyncCardPayment,
  AsyncExtensions,
  AsyncFullRepayment,
  AsyncRepaymentWireTransfer,
} from '../../Router/Router';
import useClientDebtRestructuring from '../../hooks/webapi/useClientDebtRestructuring';
import useError from '../../hooks/useError';

import { ReactComponent as BankCardIcon } from '../../assets/icons/bank-card-circle.svg';
import { ReactComponent as BankIcon } from '../../assets/icons/bank-circle.svg';
import { ReactComponent as BizumIcon } from '../../assets/icons/bizum.svg';
import { ReactComponent as CalendarIcon } from '../../assets/icons/calendar-circle.svg';
import {
  Card,
  Heading,
  Link,
  MenuButton,
  PenaltyNotification,
  Placeholder,
} from '../../components';
import { ReactComponent as CheckIcon } from '../../assets/icons/check-circle.svg';
import { dateFormat, numberToCurrency } from '../../utils/translate';
import { getBackUrlMenuScreens } from '../../utils/getUrl';
import { Helmet } from 'react-helmet-async';
import { ReactComponent as InfoIcon } from '../../assets/icons/error-info.svg';
import { memo, useEffect, useMemo, useState } from 'react';
import {
  QUERY_BACK_ROUTE,
  QUERY_BIZUM,
  QUERY_PARTIAL_AMOUNT,
} from '../../utils/constants';
import {
  useCardPayments,
  useClient,
  useLatestLoan,
  usePaymentSchedule,
} from '../../hooks/webapi';
import { useNavigate } from '@reach/router';
import { useTranslation } from 'react-i18next';
import classes from 'classnames';
import Dashboard from '../Layouts/Dashboard/Dashboard';
import dayjs from 'dayjs';
import styles from './RepaymentDefault.module.scss';

function preloadRepaymentWireTransfer() {
  AsyncRepaymentWireTransfer.preload();
}

function preloadExtensions() {
  AsyncExtensions.preload();
}

function preloadCardPayment() {
  AsyncCardPayment.preload();
}

function preloadFullRepayment() {
  AsyncFullRepayment.preload();
}

export default memo(function RepaymentDefault() {
  const { t } = useTranslation('repayment');
  const { showError } = useError();
  const { client } = useClient();
  const { paymentSchedule, fetchPaymentSchedule } = usePaymentSchedule();
  const { latestLoan } = useLatestLoan();
  const [loading, setLoading] = useState(true);
  const [navigationDisabled, setNavigationDisabled] = useState(false);
  const navigate = useNavigate();
  const { getBizumPayments } = useCardPayments();
  const { isDebtRestructuring } = useClientDebtRestructuring();

  const partialAmountSchedule =
    latestLoan?.productCode === 'CONS' &&
    paymentSchedule?.items?.find(({ status }) => status !== 'PAID')
      ?.totalAmount;

  useEffect(() => {
    preloadRepaymentWireTransfer();
    preloadExtensions();
    preloadCardPayment();
    preloadFullRepayment();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        if (!latestLoan) {
          navigate(R.REPEATED_LOAN, { replace: true });

          return;
        }

        if (latestLoan.debtCollectLoanAgreement) {
          navigate(R.ACTIVE_LOAN, { replace: true });

          return;
        }

        await fetchPaymentSchedule();

        setLoading(false);
      } catch {}
    })();
  }, [fetchPaymentSchedule, latestLoan, navigate]);

  /** The extension with the highest term */
  const maximumExtension = useMemo(
    () =>
      latestLoan?.loanExtensionFees.reduce(
        (acc: any, item) => (acc = acc.term > item.term ? acc : item),
        0,
      ),
    [latestLoan?.loanExtensionFees],
  );

  function renderPaymentInfo() {
    if (latestLoan?.productCode !== 'CONS') {
      return (
        <Card className={styles.payment} dataTest="repayment-paymentInfo">
          <div className={styles.row}>
            <Placeholder
              loading={loading}
              className={styles.bold}
              dataTest="repayment-paymentInfo-title"
            >
              {t('pay_now')}
            </Placeholder>
            <Placeholder
              loading={loading}
              dataTest="repayment-paymentInfo-value"
            >
              {numberToCurrency(latestLoan?.payNowAmount)}
            </Placeholder>
          </div>
          {!!latestLoan?.earlyFullRepaymentFee && (
            <>
              <div data-test="early-repayment-fee-data">
                <div className={styles.row}>
                  <Placeholder
                    loading={loading}
                    data-test="early-repayment-fee-data-text"
                  >
                    {t('open_amount')}
                  </Placeholder>
                  <Placeholder
                    loading={loading}
                    dataTest="early-repayment-fee-data-value"
                  >
                    {numberToCurrency(latestLoan?.openAmount)}
                  </Placeholder>
                </div>
                <div className={styles.row}>
                  <Placeholder
                    loading={loading}
                    dataTest="early-repayment-fee-data-full-text"
                  >
                    {t('early_repayment_fee')}
                  </Placeholder>
                  <Placeholder
                    loading={loading}
                    dataTest="early-repayment-fee-data-full-value"
                  >
                    {numberToCurrency(latestLoan?.earlyFullRepaymentFee)}
                  </Placeholder>
                </div>
              </div>
              <div className={styles['fee-notification']}>
                <div className={styles['fee-icon']}>
                  <InfoIcon data-test="early-repayment-fee-data-fee-notif-icon" />
                </div>
                <div
                  data-test="early-repayment-fee-data-fee-notif-text"
                  dangerouslySetInnerHTML={{
                    __html: t('early_repayment_text'),
                  }}
                ></div>
              </div>
            </>
          )}
        </Card>
      );
    }

    return (
      <Card className={styles.schedule} dataTest="repayment-schedule-card">
        <div className={styles['table-header']}>
          <Heading size="s" dataTest="repayment-schedule-heading">
            {t('monthly_payments')}
          </Heading>
        </div>
        {paymentSchedule?.items.map(
          ({ number, dueDate, totalAmount, status }, index) => (
            <div
              key={dueDate}
              className={classes(
                styles['table-row'],
                paymentSchedule?.activeInstalement === number && styles.open,
                status === 'DELAYED' && styles.delayed,
                status === 'PAID' && styles.closed,
              )}
              data-test={`repayment-schedule-item-${index}-row`}
            >
              <div className={styles['table-cell']}>
                <span>
                  <Placeholder
                    loading={loading}
                    dataTest={`repayment-schedule-item-${index}-date`}
                  >
                    {dateFormat(dueDate)}
                  </Placeholder>
                </span>
              </div>
              <div className={styles['table-cell']}>
                {status === 'PAID' && (
                  <CheckIcon
                    className={styles.check}
                    data-test={`repayment-schedule-item-${index}-check-icon`}
                  />
                )}
                <span>
                  <Placeholder
                    loading={loading}
                    dataTest={`repayment-schedule-item-${index}-totalAmount`}
                  >
                    {numberToCurrency(totalAmount)}
                  </Placeholder>
                </span>
              </div>
            </div>
          ),
        )}
      </Card>
    );
  }

  const showRepaymentButtons =
    latestLoan?.productCode !== 'CONS' ||
    latestLoan?.nearestPaymentDate <= dayjs().format('YYYY-MM-DD');

  const showPenaltyNotification =
    latestLoan &&
    (latestLoan.penaltyAmount > 0 || latestLoan.commissionAmount > 0);

  const onBizumOptionClicked = async () => {
    const successURLPayment =
      latestLoan?.productCode === 'CONS' || isDebtRestructuring
        ? process.env.REACT_APP_ROOT_URL + R.CARD_PAYMENT_CONS_SUCCESS
        : process.env.REACT_APP_ROOT_URL + R.CARD_PAYMENT_MINI_SUCCESS;

    const failureURLPayment =
      latestLoan?.productCode === 'CONS' || isDebtRestructuring
        ? process.env.REACT_APP_ROOT_URL +
          R.CARD_PAYMENT_CONS_FAILURE +
          `?${QUERY_BIZUM}&` +
          search
        : process.env.REACT_APP_ROOT_URL +
          R.CARD_PAYMENT_FAILURE +
          `?${QUERY_BIZUM}&` +
          search;

    const amount =
      latestLoan?.productCode === 'CONS'
        ? latestLoan?.payableAmount
        : latestLoan?.payNowAmount;

    if (!amount) {
      showError({ message: t('error:invalidLoan') });
    } else {
      setNavigationDisabled(true);
      setLoading(true);

      const response = await getBizumPayments({
        body: {
          amount,
          successUrl: successURLPayment,
          failureUrl: failureURLPayment,
        },
      });

      setNavigationDisabled(false);
      setLoading(false);

      if (response?.payFormUrl) {
        navigate(response?.payFormUrl);
      } else if (response?.status === 'ERROR') {
        if (response?.errorCode === 'DUPLICATE_PAYMENT' && false) {
          showError({ message: t('error:duplicatePayment') });
        } else showError();
      }
    }
  };

  const search = `${QUERY_BACK_ROUTE}=${R.REPAYMENT}${
    partialAmountSchedule
      ? `&${QUERY_PARTIAL_AMOUNT}=${partialAmountSchedule}`
      : ''
  }`;

  return (
    <Dashboard
      backUrl={getBackUrlMenuScreens(client, latestLoan)}
      backUrlDisabled={navigationDisabled}
      menuDisabled={navigationDisabled}
      small
      className={styles['body-wrapper']}
    >
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>
      <Heading className={styles.heading} size="l">
        <Placeholder loading={loading} dataTest="repayment-schedule-heading">
          {t('heading')}
        </Placeholder>
      </Heading>

      {showPenaltyNotification && latestLoan && (
        <PenaltyNotification
          loading={loading}
          penalty={latestLoan.penaltyAmount}
          overdue={latestLoan.overDueDays}
          commission={latestLoan.commissionAmount}
        />
      )}

      {!showRepaymentButtons && !showPenaltyNotification && (
        <div
          data-test="payment-notification"
          className={styles['payment-notification']}
        >
          <div className={styles['info-icon']}>
            <InfoIcon data-test="payment-notification-icon" />
          </div>
          <Placeholder loading={loading}>
            <span
              dangerouslySetInnerHTML={{
                __html: t('payment_notification', {
                  date: dateFormat(latestLoan?.nearestPaymentDate),
                }),
              }}
              data-test="payment-notification-text"
            />
          </Placeholder>
        </div>
      )}

      {renderPaymentInfo()}

      {showRepaymentButtons && (
        <Card dataTest="repayment-buttons" className={styles.menu}>
          <MenuButton
            analyticsIsTrackable={true}
            analyticsTrackingDescription="Card payment button"
            loading={loading}
            dataTest="card-payment-button"
            className={styles['menu-button']}
            heading={t('card_heading')}
            info={t('card_info')}
            Icon={BankCardIcon}
            url={`${R.CARD_PAYMENT}?${search}`}
            label={t('common:recommended')}
          />
          <MenuButton
            analyticsIsTrackable={true}
            analyticsTrackingDescription="Bizum payment button"
            loading={loading}
            dataTest="bizum-payment-button"
            className={styles['menu-button']}
            heading={t('bizum_heading')}
            info={t('bizum_info')}
            Icon={BizumIcon}
            onClick={onBizumOptionClicked}
            isNew
          />
          <MenuButton
            analyticsIsTrackable={true}
            analyticsTrackingDescription="Wire transfer button"
            loading={loading}
            dataTest="wire-transfer-button"
            className={styles['menu-button']}
            heading={t('manual_transfer_heading')}
            info={t('manual_transfer_info')}
            Icon={BankIcon}
            url={R.REPAYMENT_WIRE_TRANSFER}
          />
        </Card>
      )}
      {maximumExtension ? (
        <Card className={styles.menu}>
          <MenuButton
            analyticsIsTrackable={false}
            analyticsTrackingDescription="Extension menu button"
            loading={loading}
            dataTest="extensions-button"
            className={styles['menu-button']}
            heading={t('postpone_heading')}
            info={t('postpone_info')}
            Icon={CalendarIcon}
            url={`${R.EXTENSIONS}?PID=${latestLoan?.productNumber}`}
          />
        </Card>
      ) : null}
      {latestLoan?.productCode === 'CONS' && (
        <Link
          className={styles['helper-text-link']}
          to={R.FULL_REPAYMENT}
          dataTest="full-link-repayment"
        >
          {t('footer:payFullLoan')}
        </Link>
      )}
    </Dashboard>
  );
});
